import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  debounce,
  formatAuthorityForDisplay,
  useAuthorityApi,
  buildAuthorityQuery,
} from '../utils';
import { getAuthorityTypeForField } from '../../../constants';
import {
  getSearchIndexFromAuthorityType,
  searchableIndexesValues,
} from '../../../constants/searchableIndexes';

const getSearchTypeFromIndex = (searchIndex) => {
  switch (searchIndex) {
    case searchableIndexesValues.PERSONAL_NAME:
      return 'personalName';
    case searchableIndexesValues.CORPORATE_NAME:
      return 'corporateName';
    case searchableIndexesValues.MEETING_NAME:
      return 'meetingName';
    case searchableIndexesValues.UNIFORM_TITLE:
      return 'uniformTitle';
    case searchableIndexesValues.SUBJECT:
      return 'subject';
    case searchableIndexesValues.GEOGRAPHIC_NAME:
      return 'geographicName';
    case searchableIndexesValues.NAME_TITLE:
      return 'nameTitle';
    case searchableIndexesValues.GENRE:
      return 'genre';
    default:
      return 'all';
  }
};

export const useAuthoritySuggestions = (
  fieldPath,
  inputValue,
  isAuthorityField = false,
  tenantId = null,
  defaultCount = 10,
  selectedSourceId = null,
  formValues = {},
  arrayIndex = 0,
  controlledVocab = {},
  contributorNameTypeId = null
) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentLimit, setCurrentLimit] = useState(defaultCount);
  const [hasMore, setHasMore] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [shouldShowCreationButtons, setShouldShowCreationButtons] = useState(false);

  const { searchAuthorityWithFallback } = useAuthorityApi(tenantId);

  const shouldSearch =
    isAuthorityField && inputValue && inputValue.trim().length >= 3;

  const searchFunctionRef = useRef(searchAuthorityWithFallback);
  searchFunctionRef.current = searchAuthorityWithFallback;

  const searchType = useMemo(() => {
    if (!isAuthorityField) return 'all';

    let updatedFormValues = formValues;
    if (fieldPath === 'contributors.name' && contributorNameTypeId) {
      updatedFormValues = { ...formValues };
      if (!updatedFormValues.instanceData) updatedFormValues.instanceData = {};
      if (!updatedFormValues.instanceData.contributors) updatedFormValues.instanceData.contributors = [];
      if (!updatedFormValues.instanceData.contributors[arrayIndex]) updatedFormValues.instanceData.contributors[arrayIndex] = {};

      updatedFormValues.instanceData.contributors[arrayIndex] = {
        ...updatedFormValues.instanceData.contributors[arrayIndex],
        contributorNameTypeId,
      };
    }

    const authorityConfig = getAuthorityTypeForField(
      fieldPath,
      updatedFormValues,
      arrayIndex,
      controlledVocab,
    );
    const searchIndex = getSearchIndexFromAuthorityType(authorityConfig);
    return getSearchTypeFromIndex(searchIndex);
  }, [
    isAuthorityField,
    fieldPath,
    formValues,
    arrayIndex,
    controlledVocab,
    contributorNameTypeId,
  ]);

  const debouncedSearch = useCallback(
    debounce(
      async (
        query,
        limit = currentLimit,
        sourceId = null,
        currentSearchType = 'all',
      ) => {
        setIsLoading(true);
        setError(null);
        setShouldShowCreationButtons(false);

        try {
          // Use unified search with fallback - pass searchType directly
          const response = await searchFunctionRef.current(
            query,
            currentSearchType,
            limit,
          );
          

          const formattedSuggestions = response.authorities
            .map(formatAuthorityForDisplay)
            .filter(Boolean);

          // Update total records and check if there are more results
          setTotalRecords(response.totalRecords || 0);
          setHasMore(
            formattedSuggestions.length < (response.totalRecords || 0),
          );
          setSuggestions(formattedSuggestions);

          setShouldShowCreationButtons(formattedSuggestions.length === 0);
        } catch (err) {
          setError(err.message || 'Failed to fetch authority suggestions');
          setSuggestions([]);
          setHasMore(false);
          setTotalRecords(0);
          setShouldShowCreationButtons(true);
        } finally {
          setIsLoading(false);
        }
      },
      500,
    ),
    []
  );

  useEffect(() => {
    if (shouldSearch) {
      debouncedSearch(
        inputValue.trim(),
        currentLimit,
        selectedSourceId,
        searchType,
      );
    } else {
      setSuggestions([]);
      setIsLoading(false);
      setTotalRecords(0);
    }
  }, [
    inputValue,
    fieldPath,
    shouldSearch,
    selectedSourceId,
    searchType,
    debouncedSearch,
  ]);

  useEffect(() => {
    if (!isAuthorityField) {
      setSuggestions([]);
      setIsLoading(false);
      setCurrentLimit(defaultCount);
      setHasMore(false);
      setTotalRecords(0);
    }
  }, [isAuthorityField, defaultCount]);

  const loadMoreSuggestions = useCallback(() => {
    if (hasMore && !isLoading && shouldSearch) {
      let increment = defaultCount;
      if (defaultCount >= 50) {
        increment = 25;
      } else if (defaultCount >= 30) {
        increment = 20;
      } else {
        increment = defaultCount;
      }

      const newLimit = currentLimit + increment;
      setCurrentLimit(newLimit);
      debouncedSearch(
        inputValue.trim(),
        newLimit,
        selectedSourceId,
        searchType,
      );
    }
  }, [
    hasMore,
    isLoading,
    shouldSearch,
    currentLimit,
    defaultCount,
    fieldPath,
    inputValue,
    selectedSourceId,
    searchType,
    debouncedSearch,
  ]);

  useEffect(() => {
    if (inputValue.trim().length >= 3) {
      setCurrentLimit(defaultCount);
    }
  }, [inputValue, defaultCount]);

  useEffect(() => {
    setCurrentLimit(defaultCount);
    if (shouldSearch && inputValue.trim().length >= 3) {
      debouncedSearch(
        inputValue.trim(),
        defaultCount,
        selectedSourceId,
        searchType,
      );
    }
  }, [
    defaultCount,
    shouldSearch,
    inputValue,
    fieldPath,
    selectedSourceId,
    searchType,
    debouncedSearch,
  ]);

  return {
    suggestions,
    isLoading,
    error,
    hasSuggestions: suggestions.length > 0,
    hasMore,
    loadMoreSuggestions,
    totalRecords,
    shouldShowCreationButtons,
    currentQuery: shouldSearch
      ? inputValue.trim()
      : null,
  };
};
